import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import DonutChart from "@/components/dashboard/DonutChart";

function Panel({ n, title, children, className = "" }) {
  return (
    <div className={`bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow ${className}`}>
      <div className="flex items-center mb-4">
        <span className="text-[#93c5fd] mr-2 font-black text-lg">{n}.</span>
        <span className="text-[#1e40af] font-bold text-sm uppercase tracking-tight">{title}</span>
      </div>
      {children}
    </div>
  );
}

export default function FailurePage() {
  const { params } = useFilters();
  const [modes, setModes] = useState({ items: [], total_downtime_min: 0 });
  const [failures, setFailures] = useState([]);

  useEffect(() => {
    (async () => {
      const [m, f] = await Promise.all([
        api.get("/metrics/failure_modes", { params: { ...params, top: 8 } }),
        api.get("/failures", { params: { ...params, limit: 5000 } }),
      ]);
      setModes(m.data); setFailures(f.data.items);
    })();
  }, [params.start, params.end, params.line, params.subsystem]);

  // frequency-based ranking of failure modes
  const modeFreq = useMemo(() => {
    const map = new Map();
    failures.forEach(f => {
      const k = f.kejadian || "Unknown";
      const g = map.get(k) || { mode: k, count: 0, downtime: 0 };
      g.count++; g.downtime += (f.delay_seconds || 0) / 60;
      map.set(k, g);
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count).slice(0, 15);
  }, [failures]);

  const totalFailures = failures.length;
  const topMode = modeFreq[0];

  return (
    <div className="space-y-6 pb-8" data-testid="detail-failure">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl border border-slate-200/60 kpi-shadow">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Unique Failure Modes</div>
          <div className="text-3xl font-black text-slate-900 mt-1">{modeFreq.length}</div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-slate-200/60 kpi-shadow">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Most Frequent Mode</div>
          <div className="text-lg font-black text-[#1e40af] mt-1 truncate">{topMode?.mode || "—"}</div>
          <div className="text-xs text-slate-500 mt-0.5">{topMode ? `${topMode.count} events` : ""}</div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-slate-200/60 kpi-shadow">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Total Downtime</div>
          <div className="text-3xl font-black text-[#f59e0b] mt-1">{Math.round(modes.total_downtime_min).toLocaleString()}<span className="text-sm ml-1 text-slate-400">min</span></div>
        </div>
      </div>

      <Panel n="1" title="Top Failure Modes by Downtime">
        <DonutChart items={modes.items} />
      </Panel>

      <Panel n="2" title="Failure Mode Frequency Ranking">
        <div className="overflow-x-auto trams-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="text-left font-semibold pb-3">#</th>
                <th className="text-left font-semibold pb-3">Failure Mode</th>
                <th className="text-right font-semibold pb-3">Events</th>
                <th className="text-right font-semibold pb-3">Frequency (%)</th>
                <th className="text-right font-semibold pb-3">Downtime (Min)</th>
                <th className="text-left font-semibold pb-3">Bar</th>
              </tr>
            </thead>
            <tbody>
              {modeFreq.map((m, i) => {
                const pct = totalFailures > 0 ? (m.count / totalFailures * 100) : 0;
                return (
                  <tr key={m.mode} className="border-b border-slate-100">
                    <td className="py-2.5 font-black text-slate-400 tabular-nums w-10">#{i + 1}</td>
                    <td className="py-2.5 font-medium text-slate-900 max-w-[260px] truncate" title={m.mode}>{m.mode}</td>
                    <td className="py-2.5 text-right tabular-nums font-semibold">{m.count}</td>
                    <td className="py-2.5 text-right tabular-nums text-slate-600">{pct.toFixed(1)}%</td>
                    <td className="py-2.5 text-right tabular-nums text-slate-600">{Math.round(m.downtime)}</td>
                    <td className="py-2.5 w-40">
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#1e40af] to-[#3b82f6] rounded-full" style={{ width: `${Math.min(pct * 3, 100)}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
